/**
 * TabComebackMessage Component
 * 
 * Blinks a custom message in the browser tab title when the tab is inactive.
 * 
 * Settings (configured in Admin > Settings > General):
 * - site_tab_comeback_enabled: Enable/disable the feature
 * - site_tab_comeback_title: Message to blink in tab when inactive
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSettings } from '../context/SettingsContext';

const TabComebackMessage = () => {
  const { settings, loading } = useSettings();
  const originalTitleRef = useRef(document.title);
  const blinkIntervalRef = useRef(null);
  const isBlinkingRef = useRef(false);
  
  // Blink interval in ms
  const BLINK_INTERVAL = 1000;
  
  const startBlinking = useCallback(() => {
    if (isBlinkingRef.current) return;
    
    const customTitle = settings.site_tab_comeback_title;
    if (!customTitle) return;
    
    isBlinkingRef.current = true;
    let showCustom = true;
    
    // Start blinking between original title and custom message
    blinkIntervalRef.current = setInterval(() => {
      if (showCustom) {
        document.title = customTitle;
      } else {
        document.title = originalTitleRef.current;
      }
      showCustom = !showCustom;
    }, BLINK_INTERVAL);
  }, [settings.site_tab_comeback_title]);
  
  const stopBlinking = useCallback(() => {
    if (blinkIntervalRef.current) {
      clearInterval(blinkIntervalRef.current);
      blinkIntervalRef.current = null;
    }
    isBlinkingRef.current = false;
    // Restore original title
    document.title = originalTitleRef.current;
  }, []);
  
  const handleVisibilityChange = useCallback(() => {
    // Skip if feature is disabled or still loading
    if (loading || !settings.site_tab_comeback_enabled) return;
    
    if (document.hidden) {
      // Tab is now hidden - store original title and start blinking
      originalTitleRef.current = document.title;
      startBlinking();
    } else {
      // Tab is now visible - stop blinking
      stopBlinking();
    }
  }, [settings, loading, startBlinking, stopBlinking]);
  
  useEffect(() => {
    // Store initial title
    originalTitleRef.current = document.title;
    
    // Add event listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopBlinking();
    };
  }, [handleVisibilityChange, stopBlinking]);
  
  // This component doesn't render anything
  return null;
};

export default TabComebackMessage;
